import { Button, Dialog, DialogActions, Box, DialogContent, IconButton, DialogTitle, FormHelperText, TextField, Divider, Alert } from '@mui/material';
import { setSuccessfullModifyDialog } from '../../../redux/successfullModifyDialogSlice';
import { setContractDetailsData } from '../../../redux/contractDetailsDataSlice';
import { CustomErrorLogger } from '../../../utils/CustomErrorLogger';
import { useSelector, useDispatch } from 'react-redux';
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Iconify from '../../Iconify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import '../../../css/contracts/editContractPeriod.css';

export const EditContractPeriod = ({ openEditPeriod, setOpenEditPeriod }) => {

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [errMsg, setErrMag] = useState(null);
    const [errPeriod, setErrPeriod] = useState(null);
    const contractDetailsData = useSelector((state) => state.ContractDetailsData);

    // Sort contract dates by start_date
    const sortedContractDates = [...contractDetailsData.contract_date].sort((a, b) =>
        new Date(a.start_date) - new Date(b.start_date)
    );

    const formik = useFormik({
        initialValues: {
            periods: sortedContractDates.map(period => ({
                id: period.id,
                start_date: period.start_date,
                end_date: period.end_date
            }))
        },

        validationSchema: Yup.object().shape({
            periods: Yup.array().of(
                Yup.object().shape({
                    start_date: Yup.date().required(t('isRequired')),
                    end_date: Yup.date()
                        .nullable()
                        .test(
                            'isRequiredIfNotLatest',
                            t('isRequired'),
                            function (end_date) {
                                const { start_date } = this.parent; // Current period's start_date
                                const allPeriods = this.options.context.periods; // Access all periods from context

                                // Find the latest start_date
                                const latestStartDate = new Date(
                                    Math.max(...allPeriods.map(p => new Date(p.start_date).getTime()))
                                );

                                // Check if the current start_date is the latest
                                const isLatest = new Date(start_date).getTime() === latestStartDate.getTime();

                                // If it's not the latest, ensure end_date is not null
                                return isLatest || (!isLatest && end_date);
                            }
                        )
                })
            )
        }),

        onSubmit: async (values) => {
            try {
                // Validate no date overlaps
                const hasOverlaps = checkForDateOverlaps(values.periods);
                if (hasOverlaps) {
                    setErrPeriod('Les périodes ne doivent pas se chevaucher');
                    return;
                }

                if (values.periods.length === 0) {
                    return;
                }

                const period_ids_to_delete = contractDetailsData.contract_date
                    .filter(cd => !values.periods.map(p => p.id).includes(cd.id))
                    .map(cd => cd.id);

                const data = {
                    periods: values.periods,
                    period_ids_to_delete
                }
                const responses = await axios.patch(`contract-date/${contractDetailsData.id}`, data)

                if (responses.status === 200 || responses.status === 201) {
                    const openDate = values.periods.find((period) => !period.end_date)
                    dispatch(setSuccessfullModifyDialog({ open: true, type: "Modifier les périodes du contrat" }));

                    dispatch(setContractDetailsData({
                        ...contractDetailsData,
                        auto_renew: openDate ? false : contractDetailsData.auto_renew,
                        rappel_frequency: openDate ? 0 : contractDetailsData.rappel_frequency,
                        contract_date: responses.data.contract_date.map((newDate) => {
                            const existingDate = contractDetailsData.contract_date.find(
                                (existing) => existing.id === newDate.id
                            );
                            return existingDate
                                ? { ...newDate, contract_machine: existingDate.contract_machine }
                                : newDate;
                        }),
                    }));

                    setOpenEditPeriod(false);
                }
            } catch (error) {
                console.error('Error updating contract periods:', error);
                CustomErrorLogger(error, 'Error updating contract periods');
                setErrMag(error?.response?.data?.message || error.message || "erreur lors de la modification des périodes du contrat");
            }
        },
    });

    const { handleSubmit, values, handleBlur, touched, errors, setFieldValue } = formik;

    const handleClose = useCallback(() => {
        setOpenEditPeriod(false);
    }, [setOpenEditPeriod]);

    const checkForDateOverlaps = (periods) => {
        const sortedPeriods = [...periods]
            .filter(p => p.start_date && p.end_date)
            .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

        for (let i = 1; i < sortedPeriods.length; i++) {
            const prevEnd = new Date(sortedPeriods[i - 1].end_date);
            const currStart = new Date(sortedPeriods[i].start_date);

            if (currStart <= prevEnd) {
                return true;
            }
        }
        return false;
    };

    const handlePeriodChange = (index, field, value) => {
        const newPeriods = [...values.periods];

        // If the field being changed is a date, ensure it is formatted correctly
        if (field === 'start_date' || field === 'end_date') {
            newPeriods[index][field] = value;
        } else {
            newPeriods[index][field] = value;
        }

        setFieldValue('periods', newPeriods);
    };

    const handleAddPeriod = () => {
        // Get the last period's end date or use today as default
        const lastPeriod = values.periods[values.periods.length - 1];
        const defaultStartDate = lastPeriod?.end_date
            ? new Date(new Date(lastPeriod.end_date).getTime() + 86400000) // +1 day
            : new Date();

        const newPeriod = {
            id: null,
            start_date: defaultStartDate,
            end_date: null // Use null for an empty end_date
        };

        setFieldValue('periods', [...values.periods, newPeriod]);
    };

    const handleDeletePeriod = (index) => {
        const newPeriods = values.periods.filter((_, i) => i !== index);
        setFieldValue('periods', newPeriods);
    }

    return (
        <Dialog open={openEditPeriod} fullWidth maxWidth="md">
            <IconButton onClick={handleClose} className='editContract_icon_button'>
                <Iconify icon="material-symbols:close" className='editContract_close_icon' />
            </IconButton>
            <DialogTitle className='editContract_dialog_title'>
                Modifier toutes les périodes du contrat
            </DialogTitle>
            <Divider />
            {errMsg && <Alert severity="error">{errMsg}</Alert>}
            <form onSubmit={handleSubmit}>
                <DialogContent className='editContract_dialog_content'>
                    {errPeriod && <Alert severity="warning">{errPeriod}</Alert>}

                    {values.periods.map((period, index) => {
                        const periodErrors = errors.periods?.[index] || {};
                        const periodTouched = touched.periods?.[index] || {};
                        // Find the latest start_date
                        const latestStartDate = new Date(
                            Math.max(...values.periods.map(p => new Date(p.start_date).getTime()))
                        );
                        // Check if the current start_date is the latest
                        const isLatest = new Date(period.start_date).getTime() === latestStartDate.getTime();
                        return (
                            <Box key={period.id || `new-period-${index}`} className="editContract_period_box">
                                <Box className="editContract_period_start_end_date_fields">
                                    <Box className='editContractPeriod_helper_field_box'>
                                        <FormHelperText className="editContractInputTitle">
                                            Date de début
                                        </FormHelperText>
                                        <TextField
                                            fullWidth
                                            type='date'
                                            className={`editContract_InputField`}
                                            value={period.start_date ? new Date(period.start_date).toISOString().split('T')[0] : ''}
                                            onChange={(e) => handlePeriodChange(index, 'start_date', e.target.value)}
                                            onBlur={handleBlur}
                                            name={`periods[${index}].start_date`}
                                            error={periodTouched.start_date && Boolean(periodErrors.start_date)}
                                            helperText={periodTouched.start_date && periodErrors.start_date}
                                        />
                                    </Box>
                                    <Box className='editContractPeriod_helper_field_box'>
                                        <FormHelperText className="editContractInputTitle">
                                            Date de fin {isLatest && '(Optionnel)'}
                                        </FormHelperText>
                                        <TextField
                                            fullWidth
                                            type='date'
                                            className={`editContract_InputField`}
                                            value={period.end_date ? new Date(period.end_date).toISOString().split('T')[0] : ''}
                                            onChange={(e) => handlePeriodChange(index, 'end_date', e.target.value)}
                                            onBlur={handleBlur}
                                            name={`periods[${index}].end_date`}
                                            error={periodTouched.end_date && Boolean(periodErrors.end_date)}
                                            helperText={periodTouched.end_date && periodErrors.end_date}
                                        />
                                    </Box>
                                    {!(index === 0 && values.periods.length === 1) &&
                                        <Iconify icon='ic:outline-delete' className="editContractPeriod_delete_icon" onClick={() => handleDeletePeriod(index)} />
                                    }
                                </Box>
                                <Divider />
                            </Box>
                        );
                    })}
                </DialogContent>
                <DialogActions className='editContract_dialog_actions'>
                    <Button className='editContract_button_annuler' onClick={handleClose}>{t('Annuler')}</Button>
                    <Box className='editContract_ajouter_valider_btn_box'>
                        <Button className='editContract_button_ajouter' variant="contained" onClick={handleAddPeriod}>Ajouter une période</Button>
                        <Button className='editContract_button_valider' variant="contained" type="submit">{t('Valider')}</Button>
                    </Box>
                </DialogActions>
            </form>
        </Dialog>
    );
};